/**
 * Плашка «это обновлённый AiSearch» на русской витрине.
 *
 * Сайт переехал на новую версию, и человек, много лет ходивший на
 * прежнюю, попадает на незнакомую страницу без предупреждения. Плашка
 * объясняет, что произошло, и даёт два выхода: пожаловаться и вернуться
 * на прежнюю версию. Без неё он просто уходит, и мы даже не узнаём, что
 * именно ему не подошло.
 *
 * Только сервер, никакого JS. Плашка обязана быть в первом же HTML: если
 * рисовать её скриптом, человек на медленной связи увидит незнакомый
 * сайт раньше объяснения, а тот, у кого скрипты отключены, не увидит
 * объяснения вовсе. По той же причине закрытие сделано обычной ссылкой,
 * а не обработчиком события.
 */

/** Кука, которой человек говорит «плашку больше не показывать». */
export const KUKA_PLASHKI = 'ais_plashka_skryta';

/**
 * Заголовки, которыми middleware сообщает странице её настоящий адрес.
 *
 * Страница своего адреса не знает: middleware переписывает запрос во
 * внутреннюю ветку (`/ru/aisearch/aisearch-ru/...`), и всё, до чего
 * страница может дотянуться, это переписанный адрес. Ссылке «прежняя
 * версия» нужен настоящий: она обязана вести на ту же страницу старого
 * сайта, а из внутренней ветки её путь не собрать.
 */
export const PUT_HEADER = 'x-ais-put';
export const ZAPROS_HEADER = 'x-ais-zapros';

/**
 * Плашка только старичкам (Seo-marketing, 30.10.2026): по ней ушли на
 * прежнюю версию 176 визитов, 109 из них с главной, и это в основном
 * новые люди из поиска — им «вернитесь на прежнюю версию» незачем.
 *
 * Старичок — тот, кто был на aisearch.ru до переезда на новую версию
 * (ночь на 22.09.2026). Узнаём по кукам Метрики на домене aisearch.ru:
 * `_ym_d` — время первого визита в секундах, у `_ym_uid` первые 10 цифр
 * — то же время. Счётчик стоял и на прежней версии, так что у её
 * посетителей эти куки старше переезда. Решает браузер: страница
 * статическая (#212), сервер куки не читает. Почистил куки — плашки не
 * увидит; это дешевле, чем показывать её всем.
 */
export const PEREEZD_SEKUND = 1789948800; // 21.09.2026 00:00 UTC

export function staryjPosetitel(kuki: string): boolean {
  for (const para of kuki.split('; ')) {
    const i = para.indexOf('=');
    const imya = para.slice(0, i);
    const znachenie = para.slice(i + 1);
    const t =
      imya === '_ym_d'
        ? Number(znachenie)
        : imya === '_ym_uid'
          ? Number(znachenie.slice(0, 10))
          : NaN;
    if (t > 1e9 && t < PEREEZD_SEKUND) return true;
  }
  return false;
}

/**
 * Тот же выбор встроенным скриптом до отрисовки плашки: закрыта или не
 * старичок — на <html> класс, который её прячет. Повторяет
 * `staryjPosetitel`; совпадение держит тест.
 */
export const SKRIPT_PLASHKI = `(function(){var c=document.cookie.split('; '),o=false;if(c.indexOf('${KUKA_PLASHKI}=1')<0)c.forEach(function(p){var i=p.indexOf('='),n=p.slice(0,i),v=p.slice(i+1),t=n==='_ym_d'?+v:n==='_ym_uid'?+v.slice(0,10):NaN;if(t>1e9&&t<${String(PEREEZD_SEKUND)})o=true});if(!o)document.documentElement.classList.add('ais-plashka-skryta')})();`;

/** Год: выбор «закрыть» не должен забываться через неделю. */
export const SROK_KUKI_SEKUND = 365 * 24 * 60 * 60;

/**
 * Прежняя версия сайта.
 *
 * Отдельный домен, а не раздел: там работает старое приложение целиком,
 * со своей базой и своими адресами.
 */
export const LEGASI_SITE = 'https://2024.aisearch.ru';

/**
 * Режим восстановления после сбоя у хостинг-провайдера (03.11.2026).
 *
 * Пока он включён, вместо «это обновлённый AiSearch» всем, а не только
 * старичкам, показывается сообщение о восстановлении: часть данных после
 * сбоя может не отображаться, и человеку нужно знать, куда написать.
 * Ссылки на прежнюю версию нет: её сайт ещё не поднят. Закрыть сообщение
 * нельзя, иначе давно закрывшие плашку его не увидят. Когда всё вернётся,
 * поставить false — плашка станет прежней.
 */
export const VOSSTANOVLENIE = true;

/** Адрес, по которому плашка закрывается. */
export const PUT_ZAKRYTIYA = '/ais-plashka/skryt';

/**
 * Адреса, которые в прежней версии действительно есть.
 *
 * Список собран из реестра перенесённых страниц (`STRANICY_INSTRUMENTOV`)
 * и проверен живыми запросами к `2024.aisearch.ru`. Нужен потому, что
 * половина нынешних адресов написана уже в новой версии
 * (`/udalit-fon-s-foto`, `/text-to-speech`, `/proverka-teksta`), и там
 * они отвечают 404.
 *
 * Копия, а не импорт реестра: реестр тянет за собой типы полей формы и
 * весь конструктор рецептов, а этому модулю нужны одни строки. Копия
 * защищена тестом, который сверяет её с реестром и падает, когда они
 * разойдутся.
 */
export const PUTI_LEGASI = new Set([
  '/gpt/write-essay',
  '/gpt/text-to-table',
  '/gpt/table-of-contents',
  '/gpt/write-mail',
  '/aimedia/text-to-voice',
  '/aimedia/image-to-text',
  '/gpt/ad-avito',
  '/gpt/bibliography',
  '/gpt/rewrite',
  '/gpt/write-text',
  '/gpt/write-small-story',
  '/gpt/write-review',
  '/gpt/answer',
  '/gpt/write-poem',
  '/gpt/write-referat',
  '/gpt/increase-text',
  '/gpt/shorten-text',
]);

/**
 * Пути, на которых плашки быть не должно.
 *
 * Служебные ответы (robots, sitemap, llms) это не страницы, а файлы для
 * машин. Ветка `/api/` тем более: там JSON, и вставленный в него HTML
 * сломал бы разбор у клиента.
 */
const SLUZHEBNYE = [
  '/api/',
  '/_next/',
  '/robots.txt',
  '/sitemap.xml',
  '/llms.txt',
  '/manifest.json',
  '/sw.js',
  PUT_ZAKRYTIYA,
];

/**
 * Показывать ли плашку.
 *
 * Три условия, и все обязательны: русская витрина (на .tech прежней
 * версии не было вовсе), человек её не закрывал, и это обычная
 * страница, а не служебный ответ.
 */
export function pokazyvatPlashku(opisanie: {
  ruDomen: boolean;
  skryta: boolean;
  put: string;
}): boolean {
  if (!opisanie.ruDomen) return false;
  if (opisanie.skryta) return false;
  return !SLUZHEBNYE.some((sluzhebnyj) => opisanie.put.startsWith(sluzhebnyj));
}

/**
 * Адрес той же страницы в прежней версии.
 *
 * Именно той же, а не главной. Человек был на `/gpt/write-essay`, нажал
 * «прежняя версия» и должен попасть на `/gpt/write-essay` там. Отправить
 * его на главную значит отнять и контекст, и терпение: искать свою
 * страницу заново он не станет.
 *
 * Но только если страница там есть. Половина нынешних адресов написана
 * уже в новой версии (`/udalit-fon-s-foto`, `/text-to-speech`,
 * `/proverka-teksta`), и в прежней их не было никогда: проверено живым
 * запросом, отвечают 404. Ссылка с одной страницы на несуществующую
 * страницу другого сайта хуже отсутствия ссылки, потому что обещает
 * человеку выход и приводит в тупик.
 *
 * Поэтому источник правды — реестр перенесённых страниц. Он собран из
 * адресов легаси и ровно для этого и заведён.
 *
 * Строка запроса сохраняется по той же причине, что и путь: в ней бывает
 * заполненная форма, и потерять её означает заставить человека вводить
 * всё заново.
 */
export function adresPrezhnejVersii(put: string, zapros?: string): string {
  const chistyj = put.startsWith('/') ? put : `/${put}`;

  // Главная есть всегда, остальное — только то, что было в легаси.
  if (chistyj !== '/' && !PUTI_LEGASI.has(chistyj)) return `${LEGASI_SITE}/`;

  // Пустой запрос и одинокий «?» дают разный адрес при одинаковом
  // смысле: браузеры и поисковики считают их разными страницами.
  const hvost = zapros !== undefined && zapros !== '' && zapros !== '?' ? normalizovat(zapros) : '';

  return `${LEGASI_SITE}${chistyj}${hvost}`;
}

/** Строка запроса ровно с одним ведущим «?». */
function normalizovat(zapros: string): string {
  return zapros.startsWith('?') ? zapros : `?${zapros}`;
}

/**
 * Адрес закрытия плашки с возвратом на ту же страницу.
 *
 * Закрытие это переход по ссылке, а после него человек обязан
 * оказаться там же, где был, иначе «закрыть» превращается в «уйти со
 * страницы».
 *
 * Путь возврата передаётся параметром и на той стороне проверяется:
 * принимается только внутренний адрес. Иначе ссылка вида
 * `?nazad=https://chuzhoy.site` превращает наш домен в открытый
 * перенаправитель, которым пользуются в рассылках.
 */
export function adresZakrytiya(put: string, zapros?: string): string {
  const nazad = put + (zapros !== undefined && zapros !== '' ? normalizovat(zapros) : '');
  return `${PUT_ZAKRYTIYA}?nazad=${encodeURIComponent(nazad)}`;
}

/**
 * Куда возвращать после закрытия.
 *
 * Принимается только путь внутри сайта: начинается с одного «/» и не с
 * «//». Второй случай важен: `//chuzhoy.site` браузер считает полным
 * адресом с текущей схемой, то есть уводит на чужой домен, хотя внешне
 * выглядит как наш путь.
 */
/**
 * Заголовок Location для возврата после закрытия.
 *
 * Отдаётся относительным путём, а не абсолютным адресом. Абсолютный
 * приходилось собирать из `request.url`, а там за обратным прокси стоит
 * внутренний адрес контейнера: проверено живьём на стенде, закрытие
 * уводило на `https://0.0.0.0:3000/`, то есть в никуда. Снаружи это
 * выглядело так, будто крестик ломает сайт.
 *
 * Брать домен из `x-forwarded-host` можно, но не нужно: заголовок
 * приходит от прокси, а относительный путь браузер разрешает сам, от
 * текущего адреса, и ошибиться доменом здесь нечем. Относительный
 * Location разрешён RFC 7231 и понимается всеми браузерами.
 *
 * Ровно поэтому функция общая для плашки и уведомления о cookie: два
 * места с одинаковым правилом разошлись бы при первой же правке, и
 * одно из них снова уводило бы на внутренний адрес.
 */
export function otvetVozvrata(nazad: string): {
  status: number;
  headers: Record<string, string>;
} {
  return {
    // 303, а не 307: человек уходит на другую страницу насовсем, и
    // повторять сюда запрос при обновлении не нужно. 302 тоже не
    // подходит — часть прокси кеширует его как постоянный.
    status: 303,
    headers: {
      Location: nazad,
      // Ответ не должен осесть в кеше: иначе следующий человек с того
      // же адреса получит чужое перенаправление вместо своей страницы.
      'Cache-Control': 'no-store',
      'X-Robots-Tag': 'noindex, nofollow',
    },
  };
}

export function bezopasnyjVozvrat(nazad: string | null): string {
  if (nazad === null || nazad === '') return '/';
  if (!nazad.startsWith('/')) return '/';
  if (nazad.startsWith('//')) return '/';
  // Обратный слеш браузеры тоже понимают как разделитель адреса:
  // `/\chuzhoy.site` уводит на чужой домен.
  if (nazad.startsWith('/\\')) return '/';
  return nazad;
}
